import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { StatusBadge } from '@/components/live';
import { callRpc } from '@/api/resources';
import { isSupabaseConfigured } from '@/lib/supabase';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { toast } from '@/lib/toast';
import { NewPlotConsent, PlotConsentDrawer } from '@/screens/parts/PlotConsent';

export interface ReadinessItem {
  item_order: number;
  item_key: 'farmer_kyc' | 'plot_records' | 'rights_consent' | 'programme_consent' | 'sale_consent';
  is_ready: boolean;
  detail: string;
  record_id: string | null;
}

export const READINESS_TITLES: Record<ReadinessItem['item_key'], string> = {
  farmer_kyc: 'Farmer KYC',
  plot_records: 'Plot records',
  rights_consent: 'Family / sharecropping consent',
  programme_consent: 'Programme consent',
  sale_consent: 'Sale consent',
};

/**
 * Everything a plot needs before harvest and sale, in one place and in order (owner, 2026-09-28:
 * "per plot, inside lists all consent requirements at one place"; "farmer kyc, plot records, Next
 * heading of program consents, then sale consent"). The server refuses a harvest entry or a
 * confirmed farmer sale from a plot until all four are done (setting plot.readiness_required).
 */
export function PlotChecklist({ plotId, farmerCode, plotCode, onPlotPage = false, linkOnly = false }: {
  plotId: string; farmerCode?: string | null; plotCode?: string | null; onPlotPage?: boolean;
  /** Consents are worked on the Plot consents page (owner, 2026-09-28): here they only link there. */
  linkOnly?: boolean;
}) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const mayIssue = can(profile, 'party.consent', 'create');
  const [openProgramme, setOpenProgramme] = useState<string | null>(null);
  const [startProgramme, setStartProgramme] = useState(false);
  const [openOther, setOpenOther] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Under 'party_consents' so that anything that changes a consent refreshes the checklist too.
  const items = useQuery({
    queryKey: ['party_consents', 'readiness', plotId],
    queryFn: () => callRpc<ReadinessItem[]>('fn_plot_readiness', { p_plot_id: plotId }),
    enabled: isSupabaseConfigured && Boolean(plotId),
  });

  /** The sale consent, or the one the plot's tenure asks for (family members', sharecropping). */
  async function start(type: 'harvest_sale' | 'family_consent' | 'sharecrop_consent') {
    setBusy(true);
    try {
      const id = await callRpc<string>('fn_issue_plot_consent_of', { p_plot_id: plotId, p_consent_type: type });
      await queryClient.invalidateQueries({ queryKey: ['party_consents'] });
      toast.success('Consent started. Generate the documents next.');
      setOpenOther(id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The sale consent could not be started.');
    } finally {
      setBusy(false);
    }
  }

  const rank = (row: ReadinessItem) => row.item_order * 10 + (row.item_key === 'rights_consent' ? 5 : 0);
  const rows = [...(items.data ?? [])].sort((a, b) => rank(a) - rank(b));
  const done = rows.filter((row) => row.is_ready).length;
  const total = rows.length || 4;

  function action(row: ReadinessItem) {
    if (linkOnly && ['rights_consent', 'programme_consent', 'sale_consent'].includes(row.item_key)) {
      const to = row.record_id && !/withdrawn|expired/i.test(row.detail) ? `/plot-consents?consent=${row.record_id}` : `/plot-consents?plot=${encodeURIComponent(plotCode ?? '')}`;
      return <Link to={to}>{row.record_id ? 'Open in Plot consents' : 'Start in Plot consents'}</Link>;
    }
    switch (row.item_key) {
      case 'farmer_kyc':
        return farmerCode ? <Link to={`/farmers?q=${encodeURIComponent(farmerCode)}`}>Open farmer</Link> : null;
      case 'plot_records':
        return !onPlotPage && plotCode ? <Link to={`/plots?q=${encodeURIComponent(plotCode)}`}>Open plot</Link> : null;
      case 'programme_consent':
        return row.record_id
          ? <button onClick={() => setOpenProgramme(row.record_id)} type="button">Open</button>
          : mayIssue && <button onClick={() => setStartProgramme(true)} type="button">Start</button>;
      case 'rights_consent':
      case 'sale_consent': {
        const type = row.item_key === 'sale_consent' ? 'harvest_sale' : /^Family/.test(row.detail) ? 'family_consent' : 'sharecrop_consent';
        return row.record_id && !/withdrawn|expired/i.test(row.detail)
          ? <button onClick={() => setOpenOther(row.record_id)} type="button">Open</button>
          : mayIssue && <button disabled={busy} onClick={() => void start(type)} type="button">{busy ? 'Starting...' : 'Start'}</button>;
      }
      default:
        return null;
    }
  }

  return (
    <section className="child-records">
      <div className="section-heading">
        <h3>Checklist <span className="muted">({done} of {total} done)</span></h3>
        <StatusBadge label={done === total ? 'Ready for harvest and sale' : 'Harvest and sale blocked'} tone={done === total ? 'green' : 'amber'} />
      </div>
      {items.isError && <p className="field-error">{items.error instanceof Error ? items.error.message : 'The checklist could not be read.'}</p>}
      <ul className="child-record-list">
        {rows.map((row, index) => (
          <li key={row.item_key}>
            <span className="child-record-text">
              <strong>{index + 1}. {READINESS_TITLES[row.item_key]} <StatusBadge label={row.is_ready ? 'Done' : 'To do'} tone={row.is_ready ? 'green' : 'amber'} /></strong>
              <small className="muted">{row.detail}</small>
            </span>
            {action(row)}
          </li>
        ))}
      </ul>
      {startProgramme && <NewPlotConsent onClose={() => setStartProgramme(false)} onCreated={(id) => { setStartProgramme(false); setOpenProgramme(id); }} plotId={plotId} />}
      {openProgramme && <PlotConsentDrawer consentId={openProgramme} onClose={() => setOpenProgramme(null)} />}
      {openOther && <PlotConsentDrawer consentId={openOther} onClose={() => { setOpenOther(null); void queryClient.invalidateQueries({ queryKey: ['party_consents'] }); }} />}
    </section>
  );
}
